// src/features/client-report/components/ClientReport/index.jsx
// ✅ Full Width + تاريخ موحد DD/MM/YYYY

import React, { useState, useEffect, useRef } from 'react';
import { Link }              from 'react-router-dom';
import { useReactToPrint }   from 'react-to-print';
import api                   from '../../../../api/axiosInstance';
import styles                from './ClientReport.module.css';
import StatCard              from '../../../../shared/components/ui/StatCard';
import { formatNumber, formatDate, BOOKING_TYPE_LABELS, BOOKING_STATUS_LABELS }
  from '../../../../shared/utils/formatters';
import * as XLSX             from 'xlsx';
import PrintableClientReport from '../../../../shared/components/print/PrintableClientReport';
import ClientSelect          from '../../../clients/components/ClientSelect';

function ClientReport() {
  const [clients,      setClients]      = useState([]);
  const [reportData,   setReportData]   = useState(null);
  const [settings,     setSettings]     = useState(null);
  const [filters,      setFilters]      = useState({
    client_id:  '',
    start_date: '',
    end_date:   '',
  });
  const [loadingClients, setLoadingClients] = useState(true);
  const [loadingReport,  setLoadingReport]  = useState(false);
  const [error,          setError]          = useState('');
  const [includeExpensesInPrint, setIncludeExpensesInPrint] = useState(true);

  const printableRef = useRef(null);

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoadingClients(true);
      setError('');
      try {
        const [clientsRes, settingsRes] = await Promise.all([
          api.get('/clients/'),
          api.get('/settings/'),
        ]);
        setClients(clientsRes.data);
        setSettings(settingsRes.data);
      } catch {
        setError('فشل في تحميل البيانات الأولية.');
      } finally {
        setLoadingClients(false);
      }
    };
    fetchInitialData();
  }, []);

  const handleDateChange   = (e) =>
    setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleClientChange = (clientId) =>
    setFilters(prev => ({ ...prev, client_id: clientId || '' }));

  const generateReport = async () => {
    if (!filters.client_id) { setError('يرجى اختيار عميل أولاً.'); return; }
    setLoadingReport(true);
    setError('');
    setReportData(null);
    try {
      const res = await api.get('/client-report/', { params: filters });
      setReportData(res.data);
    } catch {
      setError('حدث خطأ أثناء جلب بيانات التقرير.');
    } finally {
      setLoadingReport(false);
    }
  };

  const handleExport = () => {
    if (!reportData) { alert('يرجى توليد تقرير أولاً.'); return; }
    const clientName = clients.find(c => c.id === parseInt(filters.client_id))?.name || 'العميل';
    const wb = XLSX.utils.book_new();
    const fc = (v) => parseFloat(v).toFixed(2);

    let rows = [
      ['تقرير كشف حساب العميل'], [],
      ['اسم العميل:', clientName],
      ['فترة التقرير:',
        `${filters.start_date ? formatDate(filters.start_date) : 'البداية'} إلى ${filters.end_date ? formatDate(filters.end_date) : 'النهاية'}`],
      ['تاريخ إنشاء التقرير:', formatDate(new Date())], [],
      ['ملخص الحسابات'],
      ['البيان', 'المبلغ (دينار عراقي)'],
      ['إجمالي قيمة الحجوزات',   fc(reportData.financial_summary.total_bookings_value)],
      ['إجمالي المبالغ المدفوعة', fc(reportData.financial_summary.total_payments_received)],
      ['المبلغ المتبقي',           fc(reportData.financial_summary.remaining_balance)],
      ['عدد الحجوزات',             reportData.bookings.length],
      ['عدد الدفعات',              reportData.payments.length], [],
      ['قائمة الحجوزات'],
      ['#', 'نوع الحجز', 'تاريخ الحجز', 'الحالة', 'السعر الإجمالي', 'المدفوع', 'المتبقي', 'ملاحظات'],
    ];

    [...reportData.bookings]
      .sort((a, b) => new Date(b.booking_date) - new Date(a.booking_date))
      .forEach((b, i) => rows.push([
        i + 1,
        BOOKING_TYPE_LABELS[b.booking_type] ?? b.booking_type,
        formatDate(b.booking_date),
        BOOKING_STATUS_LABELS[b.status] ?? b.status,
        fc(b.total_price), fc(b.total_paid), fc(b.remaining_amount),
        b.notes || '—',
      ]));

    rows.push([], [], ['قائمة الدفعات'], ['#', 'تاريخ الدفعة', 'المبلغ', 'ملاحظات']);
    [...reportData.payments]
      .sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date))
      .forEach((p, i) => rows.push([i + 1, formatDate(p.payment_date), fc(p.amount), p.notes || '—']));

    const ws = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, 'كشف حساب العميل');
    XLSX.writeFile(wb, `كشف_حساب_${clientName}_${formatDate(new Date())}.xlsx`);
  };

  const handlePrint = useReactToPrint({
    contentRef:    printableRef,
    documentTitle: `Client_Report_${clients.find(c => c.id === parseInt(filters.client_id))?.name || 'Client'}`,
  });

  const clientName = clients.find(c => c.id === parseInt(filters.client_id))?.name || 'العميل';

  return (
    <div className={styles.container}>

      {/* مخفي للطباعة */}
      <div style={{ display: 'none' }}>
        <PrintableClientReport
          ref={printableRef}
          reportData={reportData}
          filters={filters}
          clientName={clientName}
          settings={settings}
          includeExpenses={includeExpensesInPrint}
        />
      </div>

      {/* ── الهيدر ── */}
      <header className={styles.header}>
        <h1>تقرير كشف حساب العميل</h1>
      </header>

      {/* ── الفلاتر ── */}
      <div className={styles.filterBox}>
        <div className={styles.filterControls}>
          <ClientSelect
            clients={clients}
            value={filters.client_id}
            onChange={handleClientChange}
            isLoading={loadingClients}
            placeholder="ابحث أو اختر عميلاً..."
          />
          <input
            type="date" name="start_date"
            value={filters.start_date}
            onChange={handleDateChange}
            title="من تاريخ"
          />
          <span>إلى</span>
          <input
            type="date" name="end_date"
            value={filters.end_date}
            onChange={handleDateChange}
            title="إلى تاريخ"
          />
          <button
            onClick={generateReport}
            disabled={loadingReport}
            className={styles.generateButton}
          >
            {loadingReport ? 'جاري التحميل...' : 'توليد التقرير'}
          </button>
        </div>

        <div className={styles.actionControls}>
          <div className={styles.printOptions}>
            <input
              type="checkbox"
              id="includeExpenses"
              checked={includeExpensesInPrint}
              onChange={(e) => setIncludeExpensesInPrint(e.target.checked)}
            />
            <label htmlFor="includeExpenses">طباعة المصاريف في التقرير</label>
          </div>
          <div className={styles.exportButtons}>
            <button
              onClick={handleExport}
              className={styles.exportButton}
              disabled={!reportData}
            >
              تصدير إلى Excel
            </button>
            <button
              onClick={handlePrint}
              className={styles.exportButton}
              disabled={!reportData}
              style={{ borderColor: 'var(--color-info)', color: 'var(--color-info-text)' }}
            >
              طباعة PDF
            </button>
          </div>
        </div>
      </div>

      {error && <p className={styles.errorMessage}>{error}</p>}

      {/* ── النتائج ── */}
      {reportData && (
        <div className={styles.resultsContainer}>
          <h2>نتائج التقرير للعميل: {clientName}</h2>

          {/* إحصائيات */}
          <div className={styles.statsGrid}>
            <StatCard
              title="إجمالي قيمة الحجوزات"
              value={reportData.financial_summary.total_bookings_value}
              unit="د.ع"
            />
            <StatCard
              title="إجمالي المبالغ المدفوعة"
              value={reportData.financial_summary.total_payments_received}
              unit="د.ع"
              color="profit"
            />
            <StatCard
              title="المبلغ المتبقي"
              value={reportData.financial_summary.remaining_balance}
              unit="د.ع"
              color="expense"
            />
          </div>

          {/* الحجوزات */}
          <div className={styles.bookingsListContainer}>
            <h3 className={styles.tableTitle}>
              تفاصيل الحجوزات ({reportData.bookings.length})
            </h3>

            {reportData.bookings.length > 0 ? (
              reportData.bookings.map(booking => (
                <div key={booking.id} className={styles.bookingGroup}>

                  {/* جدول الحجز */}
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>نوع الحجز</th>
                        <th>تاريخ الحجز</th>
                        <th>الحالة</th>
                        <th>السعر الإجمالي</th>
                        <th>الإجراءات</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td data-label="نوع الحجز">
                          {BOOKING_TYPE_LABELS[booking.booking_type] ?? booking.booking_type}
                        </td>
                        {/* ✅ DD/MM/YYYY */}
                        <td data-label="تاريخ الحجز">{formatDate(booking.booking_date)}</td>
                        <td data-label="الحالة">
                          {BOOKING_STATUS_LABELS[booking.status] ?? booking.status}
                        </td>
                        <td data-label="السعر الإجمالي">{formatNumber(booking.total_price)}</td>
                        <td data-label="الإجراءات" className={styles.actionsCell}>
                          <Link to={`/booking/${booking.id}`} className={styles.detailsButton}>
                            عرض التفاصيل
                          </Link>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  {/* المصاريف المرتبطة */}
                  {booking.expenses?.length > 0 && (
                    <div className={styles.subTableContainer}>
                      <h4 className={styles.subTableTitle}>
                        المصاريف المرتبطة ({booking.expenses.length})
                      </h4>
                      <table className={`${styles.table} ${styles.subTable}`}>
                        <thead>
                          <tr>
                            <th>حساب المصروف</th>
                            <th>الوصف</th>
                            <th>تاريخ الصرف</th>
                            <th>المبلغ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {booking.expenses.map(exp => (
                            <tr key={exp.id}>
                              <td>{exp.category_name}</td>
                              <td>{exp.description}</td>
                              {/* ✅ DD/MM/YYYY */}
                              <td>{formatDate(exp.expense_date)}</td>
                              <td>{formatNumber(exp.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* الدفعات المسجلة */}
                  {booking.payments?.length > 0 && (
                    <div className={styles.subTableContainer}>
                      <h4 className={styles.subTableTitle}>
                        الدفعات المسجلة ({booking.payments.length})
                      </h4>
                      <table className={`${styles.table} ${styles.subTable}`}>
                        <thead>
                          <tr>
                            <th>تاريخ الدفعة</th>
                            <th>المبلغ</th>
                            <th>ملاحظات</th>
                          </tr>
                        </thead>
                        <tbody>
                          {booking.payments.map(pay => (
                            <tr key={pay.id}>
                              {/* ✅ DD/MM/YYYY */}
                              <td>{formatDate(pay.payment_date)}</td>
                              <td>{formatNumber(pay.amount)}</td>
                              <td>{pay.notes || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                </div>
              ))
            ) : (
              <p style={{
                textAlign: 'center',
                padding:   'var(--space-8)',
                color:     'var(--text-muted)',
                fontSize:  'var(--font-size-sm)',
              }}>
                لا توجد حجوزات لهذا العميل في الفترة المحددة.
              </p>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

export default ClientReport;